import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import $apiJSON from 'http/apiJSON'
import { publicApi } from 'http'

const AuthContext = createContext(null);

const ACCESS_KEY = 'access_token';
const REFRESH_KEY = 'refresh_token';
const USER_KEY = 'auth_user';

function parseJSONSafe(text) {
  try {
    return JSON.parse(text);
  } catch (e) {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [access, setAccess] = useState(() => localStorage.getItem(ACCESS_KEY));
  const [refresh, setRefresh] = useState(() => localStorage.getItem(REFRESH_KEY));
  const [user, setUser] = useState(() => parseJSONSafe(localStorage.getItem(USER_KEY)));
  const [loading, setLoading] = useState(false);

  const saveTokens = ({ access: a, refresh: r }) => {
    if (a) {
      // keep axios http helpers working by storing access token under 'token'
      localStorage.setItem('token', a);
      localStorage.setItem(ACCESS_KEY, a);
      setAccess(a);
    }
    if (r) {
      localStorage.setItem(REFRESH_KEY, r);
      setRefresh(r);
    }
  };

  const clearAuth = useCallback(() => {
    console.log('🔓 CLEARING AUTHENTICATION...');
    
    // Удаляем ВСЕ возможные ключи авторизации
    const keysToRemove = [
      ACCESS_KEY,           // 'access_token'
      REFRESH_KEY,          // 'refresh_token'
      USER_KEY,             // 'auth_user'
      'token',              // используется axios
      'access',             // на всякий случай
      'refresh',            // на всякий случай
      'user'                // на всякий случай
    ];
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      console.log(`  ❌ Removed: ${key}`);
    });
    
    // Очищаем state
    setAccess(null);
    setRefresh(null);
    setUser(null);
    
    // Проверяем что всё удалено
    console.log('✅ CLEARED. Remaining tokens check:', {
      access_token: localStorage.getItem(ACCESS_KEY),
      refresh_token: localStorage.getItem(REFRESH_KEY),
      token: localStorage.getItem('token'),
      auth_user: localStorage.getItem(USER_KEY)
    });
  }, []);

  const fetchProfile = useCallback(async (token) => {
    console.log('🔄 fetchProfile called');
    try {
      // use $apiJSON which will read token from localStorage
      // Добавляем timestamp чтобы избежать кеширования
      const res = await $apiJSON.get(`userdata/?t=${Date.now()}`);
      const data = res.data;
      console.log('✅ Profile data received:', data);
      console.log('   - Email:', data.email);
      console.log('   - is_verified:', data.is_verified);
      
      // Сохраняем в localStorage и state
      localStorage.setItem(USER_KEY, JSON.stringify(data));
      setUser(data);
      
      console.log('✅ User state updated in AuthContext');
      return data;
    } catch (e) {
      console.error('❌ fetchProfile error:', e);
      
      // Если получили 401 или 404 (пользователь не найден/не авторизован)
      if (e.response && (e.response.status === 401 || e.response.status === 404)) {
        console.warn('⚠️ User not found or unauthorized. Clearing auth data...');
        clearAuth();
      }
      
      return null;
    }
  }, [clearAuth]);

  useEffect(() => {
    // On mount, if we have access token but no user, try fetch profile
    if (access && !user) {
      console.log('🔍 Access token found but no user. Fetching profile...');
      fetchProfile(access);
    }
    
    // Также проверяем при каждой загрузке страницы если есть токен
    if (access && user) {
      console.log('✓ User already loaded, verifying it still exists...');
      fetchProfile(access).catch(() => {
        console.log('Failed to verify user, clearing auth');
      });
    }
  }, []); // Выполняется только при монтировании компонента

  const login = useCallback(async ({ email, password }) => {
    setLoading(true);
    try {
      const res = await $apiJSON.post('account/token/', { username: email, password });
      const data = res.data;
      saveTokens({ access: data.access, refresh: data.refresh });
      await fetchProfile(data.access);
      setLoading(false);
      return { ok: true };
    } catch (e) {
      setLoading(false);
      return { ok: false, error: e.response ? e.response.data : e };
    }
  }, [fetchProfile]);

  const register = useCallback(async (formData) => {
    setLoading(true);
    console.log('📝 REGISTRATION STEP 1: Creating user and sending verification code');
    
    try {
      const res = await publicApi.post('account/register/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      console.log('✅ Step 1 successful!');
      console.log('   Response status:', res.status);
      console.log('   Response data:', res.data);
      
      setLoading(false);
      
      // Axios автоматически считает статусы 2xx успешными
      // Если мы здесь, значит запрос успешен
      return { ok: true, data: res.data };
    } catch (e) {
      console.error('❌ Registration error:', e.response?.data || e.message);
      setLoading(false);
      return { ok: false, error: e.response?.data || e.message || 'Ошибка регистрации' };
    }
  }, []);
  
  const verifyRegistration = useCallback(async (email, code) => {
    setLoading(true);
    console.log('📝 REGISTRATION STEP 2: Verifying email code');
    
    try {
      const res = await publicApi.post('account/verify-email/', { email, code });
      console.log('✅ Step 2 successful:', res.data);
      
      // Сохраняем токены
      if (res.data.access && res.data.refresh) {
        saveTokens({ access: res.data.access, refresh: res.data.refresh });
      }
      
      // Сохраняем пользователя
      if (res.data.user) {
        localStorage.setItem(USER_KEY, JSON.stringify(res.data.user));
        setUser(res.data.user);
      }
      
      setLoading(false);
      return { ok: true, data: res.data };
    } catch (e) {
      console.error('❌ Verification error:', e.response?.data || e.message);
      setLoading(false);
      return { ok: false, error: e.response?.data || e.message || 'Ошибка верификации' };
    }
  }, []);
  
  const resendVerificationCode = useCallback(async (email) => {
    setLoading(true);
    console.log('📧 Resending verification code');
    
    try {
      const res = await publicApi.post('account/resend-verification/', { email });
      console.log('✅ Code resent successfully:', res.data);
      setLoading(false);
      return { ok: true, data: res.data };
    } catch (e) {
      console.error('❌ Resend error:', e.response?.data || e.message);
      setLoading(false);
      return { ok: false, error: e.response?.data || e.message || 'Ошибка отправки кода' };
    }
  }, []);

  const refreshToken = useCallback(async () => {
    if (!refresh) return false;
    try {
      const res = await $apiJSON.post('account/token/refresh/', { refresh });
      const data = res.data;
      saveTokens({ access: data.access, refresh: data.refresh || refresh });
      return true;
    } catch (e) {
      clearAuth();
      return false;
    }
  }, [refresh, clearAuth]);

  const fetchWithAuth = useCallback(async (input, init = {}) => {
    const headers = new Headers(init.headers || {});
    if (access) headers.set('Authorization', `Bearer ${access}`);
    headers.set('Accept', 'application/json');
    const res = await fetch(input, { ...init, headers });
    if (res.status === 401) {
      // try refresh
      const refreshed = await refreshToken();
      if (!refreshed) throw new Error('Unauthorized');
      const newAccess = localStorage.getItem(ACCESS_KEY);
      const headers2 = new Headers(init.headers || {});
      if (newAccess) headers2.set('Authorization', `Bearer ${newAccess}`);
      headers2.set('Accept', 'application/json');
      return fetch(input, { ...init, headers: headers2 });
    }
    return res;
  }, [access, refreshToken]);

  const googleLogin = useCallback(async (accessToken) => {
    setLoading(true);
    try {
      const res = await publicApi.post('account/google/callback/', { access_token: accessToken });
      const data = res.data;
      
      if (!data.access || !data.refresh || !data.user) {
        throw new Error('Некорректный ответ сервера');
      }
      
      // Сохраняем токены
      saveTokens({ access: data.access, refresh: data.refresh });
      
      // Сохраняем данные пользователя
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      setUser(data.user);
      
      // Дополнительно загружаем свежий профиль
      try {
        await fetchProfile(data.access);
      } catch (profileErr) {
        console.warn('Profile fetch after Google login failed (non-critical):', profileErr);
        // Не критично, у нас уже есть данные пользователя
      }
      
      setLoading(false);
      return { ok: true, user: data.user, message: data.message, isNewUser: data.is_new_user };
    } catch (e) {
      setLoading(false);
      const errorMsg = e.response?.data?.error || e.message || 'Ошибка входа через Google';
      return { ok: false, error: errorMsg };
    }
  }, [fetchProfile]);

  const logout = useCallback(async () => {
    console.log('🚪 ========== LOGOUT INITIATED ==========');
    
    // Сохраняем токены ДО очистки
    const refreshTokenValue = localStorage.getItem(REFRESH_KEY);
    const accessTokenValue = access || localStorage.getItem(ACCESS_KEY);
    
    console.log('📋 Tokens before logout:', {
      access: accessTokenValue ? `${accessTokenValue.substring(0, 20)}...` : 'null',
      refresh: refreshTokenValue ? `${refreshTokenValue.substring(0, 20)}...` : 'null',
      user: user?.email || 'null'
    });
    
    // 1. СНАЧАЛА отправляем запрос на backend (пока токены еще есть)
    if (refreshTokenValue && accessTokenValue) {
      try {
        console.log('📤 Sending logout request to backend...');
        
        // Используем fetch напрямую чтобы контролировать заголовки
        const response = await fetch('https://security.ncore.kg/api/account/logout/', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessTokenValue}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            refresh_token: refreshTokenValue 
          })
        });
        
        const data = await response.json();
        console.log('✅ Backend logout response:', data);
      } catch (err) {
        console.error('❌ Backend logout failed (non-critical):', err.message);
        // Продолжаем даже если backend упал
      }
    } else {
      console.warn('⚠️ No tokens found, skipping backend logout');
    }
    
    // 2. ПОТОМ очищаем все данные
    console.log('🧹 Clearing all auth data from localStorage and state...');
    
    // Сначала очищаем state
    setAccess(null);
    setRefresh(null);
    setUser(null);
    
    // Затем очищаем localStorage
    const keysToRemove = [
      ACCESS_KEY, REFRESH_KEY, USER_KEY,
      'token', 'access', 'refresh', 'user'
    ];
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      console.log(`  ❌ Removed from localStorage: ${key}`);
    });
    
    // 3. Проверяем что всё удалено
    const remainingTokens = {
      localStorage_access: localStorage.getItem(ACCESS_KEY),
      localStorage_refresh: localStorage.getItem(REFRESH_KEY),
      localStorage_token: localStorage.getItem('token'),
      localStorage_user: localStorage.getItem(USER_KEY),
      state_access: access,
      state_refresh: refresh,
      state_user: user
    };
    console.log('🔍 Verification after clearAuth:', remainingTokens);
    
    // Если что-то осталось в localStorage - принудительно удаляем ВСЁ
    const hasRemainingData = keysToRemove.some(key => localStorage.getItem(key) !== null);
    if (hasRemainingData) {
      console.warn('⚠️ Some tokens remained in localStorage! Force clearing ALL localStorage...');
      localStorage.clear();
    }
    
    // 4. Перезагружаем страницу
    console.log('🔄 Redirecting to home page...');
    console.log('========== LOGOUT COMPLETED ==========');
    
    // Используем replace для гарантированного перехода
    // Это заставит браузер полностью перезагрузить страницу
    window.location.replace('/');
  }, [access, refresh, user]);

  const value = {
    access,
    refresh,
    user,
    loading,
    isAuthenticated: !!access,
    login,
    logout,
    register,
    verifyRegistration,
    resendVerificationCode,
    googleLogin,
    fetchWithAuth,
    fetchProfile,
    refreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export default AuthContext;
